import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { User, Mail, Calendar, Shield, AlertCircle, Store, Briefcase, ShoppingBag, CheckCircle, Ban } from 'lucide-react';
import { AccountActions } from './AccountActions';
import { LinkedContentSection } from './LinkedContentSection';
import { BusinessList } from './BusinessList';
import { MarketplaceItemList } from './MarketplaceItemList';
import { JobAdvertisementList } from './JobAdvertisementList';
import { ModerationAlertList } from './ModerationAlertList';
import type { UserProfileComplete, LinkedContent } from '@/types/userAdmin';

interface UserDetailProps {
  user: UserProfileComplete;
  linkedContent: LinkedContent | null;
  loading?: boolean;
  /** @deprecated Use onBan instead */
  onDisable?: (userId: string) => Promise<void>;
  /** @deprecated Use onUnban instead */
  onEnable?: (userId: string) => Promise<void>;
  onBan?: (userId: string, reason?: string) => Promise<void>;
  onUnban?: (userId: string) => Promise<void>;
}

/**
 * UserDetail component displays comprehensive user profile information
 * including linked content (businesses, marketplace items, jobs, moderation alerts)
 * Updated for Better Auth with ban/unban support
 * 
 * @param {UserDetailProps} props - Component properties
 * @returns {JSX.Element} Rendered user detail component
 */
export function UserDetail({ user, linkedContent, loading = false, onDisable, onEnable, onBan, onUnban }: UserDetailProps) {
  const { t } = useTranslation('auth');
  const [expandedSections, setExpandedSections] = useState({
    businesses: false,
    marketplace: false,
    jobs: false,
    alerts: false,
  });

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Format date for display
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  // Format roles array - roles come from top-level user.roles, not user.data.roles
  const formatRoles = (rolesData: unknown) => {
    if (!rolesData) return ['User'];
    if (Array.isArray(rolesData)) return rolesData;
    if (typeof rolesData === 'string') return [rolesData];
    return ['User'];
  };

  const roles = formatRoles(user.roles);
  // Better Auth uses status field instead of deleted_at
  const isBanned = user.status === 'banned';
  const isVerified = user.is_verified ?? false;

  // Handle ban/unban - use new props with fallback to legacy
  const handleBan = onBan || onDisable;
  const handleUnban = onUnban || onEnable;

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-neutral-200 animate-pulse rounded w-1/3" />
        <div className="space-y-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-16 bg-neutral-200 animate-pulse rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Profile Information */}
      <div>
        <h2 className="text-xl font-semibold text-neutral-900 mb-4 flex items-center gap-2">
          <User size={20} />
          {t('userAdmin.profile.title')}
        </h2>
        
        <div className="space-y-4">
          {/* Username */}
          <div className="flex items-start gap-3 p-3 bg-neutral-50 rounded-lg">
            <User className="text-neutral-600 mt-0.5" size={18} />
            <div className="flex-1">
              <p className="text-sm text-neutral-600">{t('userAdmin.profile.username')}</p>
              <p className="font-medium text-neutral-900">{user.data?.username || 'No username'}</p>
            </div>
          </div>

          {/* Email with verification status */}
          <div className="flex items-start gap-3 p-3 bg-neutral-50 rounded-lg">
            <Mail className="text-neutral-600 mt-0.5" size={18} />
            <div className="flex-1">
              <p className="text-sm text-neutral-600">{t('userAdmin.profile.email')}</p>
              <div className="flex items-center gap-2">
                <p className="font-medium text-neutral-900">{user.email}</p>
                {isVerified ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <CheckCircle size={12} />
                    {t('userAdmin.profile.verified', 'Verified')}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    {t('userAdmin.profile.unverified', 'Unverified')}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Created At */}
          <div className="flex items-start gap-3 p-3 bg-neutral-50 rounded-lg">
            <Calendar className="text-neutral-600 mt-0.5" size={18} />
            <div className="flex-1">
              <p className="text-sm text-neutral-600">{t('userAdmin.profile.created')}</p>
              <p className="font-medium text-neutral-900">{formatDate(user.created_at)}</p>
            </div>
          </div>

          {/* Last Login */}
          <div className="flex items-start gap-3 p-3 bg-neutral-50 rounded-lg">
            <Calendar className="text-neutral-600 mt-0.5" size={18} />
            <div className="flex-1">
              <p className="text-sm text-neutral-600">Last Login</p>
              <p className="font-medium text-neutral-900">{formatDate(user.last_login_at)}</p>
            </div>
          </div>

          {/* Roles */}
          <div className="flex items-start gap-3 p-3 bg-neutral-50 rounded-lg">
            <Shield className="text-neutral-600 mt-0.5" size={18} />
            <div className="flex-1">
              <p className="text-sm text-neutral-600">{t('userAdmin.profile.roles')}</p>
              <div className="flex flex-wrap gap-2 mt-1">
                {roles.map((role, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800"
                  >
                    {role}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Status */}
          <div className="flex items-start gap-3 p-3 bg-neutral-50 rounded-lg">
            {isBanned ? (
              <Ban className="text-red-600 mt-0.5" size={18} />
            ) : (
              <CheckCircle className="text-green-600 mt-0.5" size={18} />
            )}
            <div className="flex-1">
              <p className="text-sm text-neutral-600">{t('userAdmin.profile.status')}</p>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                isBanned ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
              }`}>
                {isBanned ? t('userAdmin.profile.banned', 'Banned') : t('userAdmin.profile.active')}
              </span>
            </div>
          </div>

          {/* Ban Details (only shown when banned) */}
          {isBanned && (
            <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg border border-red-200">
              <AlertCircle className="text-red-600 mt-0.5" size={18} />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800">{t('userAdmin.profile.banDetails', 'Ban Details')}</p>
                {user.ban_reason && (
                  <p className="text-sm text-red-700 mt-1">
                    <span className="font-medium">{t('userAdmin.profile.banReason', 'Reason')}:</span> {user.ban_reason}
                  </p>
                )}
                <p className="text-sm text-red-700 mt-1">
                  <span className="font-medium">{t('userAdmin.profile.banExpires', 'Expires')}:</span>{' '}
                  {user.ban_expires ? formatDate(user.ban_expires) : t('userAdmin.profile.permanent', 'Permanent')}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Linked Content */}
      <div>
        <h2 className="text-xl font-semibold text-neutral-900 mb-4">
          {t('userAdmin.linkedContent.title')}
        </h2>

        {!linkedContent ? (
          <div className="text-center py-8 text-neutral-500">
            <p>{t('userAdmin.linkedContent.loading')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Businesses */}
            <LinkedContentSection
              title={t('userAdmin.linkedContent.businesses')}
              icon={<Store size={20} />}
              count={linkedContent.businesses.count}
              expanded={expandedSections.businesses}
              onToggle={() => toggleSection('businesses')}
            >
              <BusinessList businesses={linkedContent.businesses.items} limit={10} />
            </LinkedContentSection>

            {/* Marketplace Items */}
            <LinkedContentSection
              title={t('userAdmin.linkedContent.marketplace')}
              icon={<ShoppingBag size={20} />}
              count={linkedContent.marketplace.count}
              expanded={expandedSections.marketplace}
              onToggle={() => toggleSection('marketplace')}
            >
              <MarketplaceItemList items={linkedContent.marketplace.items} limit={10} />
            </LinkedContentSection>

            {/* Jobs */}
            <LinkedContentSection
              title={t('userAdmin.linkedContent.jobs')}
              icon={<Briefcase size={20} />}
              count={linkedContent.jobs.count}
              expanded={expandedSections.jobs}
              onToggle={() => toggleSection('jobs')}
            >
              <JobAdvertisementList jobs={linkedContent.jobs.items} limit={10} />
            </LinkedContentSection>

            {/* Moderation Alerts */}
            <LinkedContentSection
              title={t('userAdmin.linkedContent.alerts')}
              icon={<AlertCircle size={20} />}
              count={linkedContent.alerts.length}
              expanded={expandedSections.alerts}
              onToggle={() => toggleSection('alerts')}
            >
              <ModerationAlertList alerts={linkedContent.alerts} limit={10} />
            </LinkedContentSection>
          </div>
        )}
      </div>

      {/* Account Actions */}
      <AccountActions
        userId={user.id}
        username={user.data?.username || null}
        isBanned={isBanned}
        onBan={handleBan}
        onUnban={handleUnban}
      />
    </div>
  );
}
